import {Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ButtonTypeEnum} from '@shared/button/button.directive';
import {AddExamFormFields, AppExamAssignDialogForm} from '@component/exam/assign-dialog/assign-dialog.form';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import {LanguageService} from '@component/language/language.service';
import {MatSelect, MatSelectChange} from '@angular/material/select';
import {MatSelectionList} from '@angular/material/list';
import {MatCheckbox} from '@angular/material/checkbox';
import {APP_DATE_FORMATS} from '@component/date/date.format';
import {AppDateAdapter} from '@component/date/date.adapter';
import {AppSelectCountsInterface} from '@component/form/input/select/select-element.directive';
import {AssignDialogAssignResultInterface, AssignDialogDataInterface, AssignDialogService, AssignDialogStateInterface} from '@component/exam/assign-dialog/assign-dialog.service';
import {map} from 'rxjs/operators';
import * as moment from 'moment';
import {GroupInterface} from '@shared/interfaces/group.interface';
import {UserInterface} from '@shared/interfaces/user.interface';
import {ResponseValidationErrorTypeGuard} from '@core/response/response-error.type-guard';
import {HttpValidationErrorResponseContextInterface} from '@core/interfaces/http-error-response.interface';

@Component({
    selector: 'app-exam-assign-dialog',
    templateUrl: './assign-dialog.component.html',
    styleUrls: ['./assign-dialog.component.scss'],
    providers: [
        AssignDialogService,
        {
            provide: MAT_DATE_LOCALE,
            useFactory: (languageService: LanguageService) => languageService.currentLanguage.pipe(
                map(locale => locale.split('_')[0]),
            ),
            deps: [LanguageService],
        },
        {provide: DateAdapter, useClass: AppDateAdapter},
        {provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS},
    ],
})
export class AppExamAssignDialogComponent implements OnInit, OnDestroy {
    readonly #assignDialogService: AssignDialogService;

    readonly #dialogRef: MatDialogRef<AppExamAssignDialogComponent>;

    readonly buttonType = ButtonTypeEnum;

    readonly data: AssignDialogDataInterface;

    @ViewChild('startHourSelect')
    startHourSelect!: MatSelect;

    @ViewChild('startMinuteSelect')
    startMinuteSelect!: MatSelect;

    @ViewChild(MatSelectionList)
    list!: MatSelectionList;

    @ViewChild('selectAllCheckbox')
    selectAllCheckbox!: MatCheckbox;

    examAssignForm!: AppExamAssignDialogForm;

    minDate: moment.Moment = moment().subtract(20, 'years').startOf('year');

    maxDate: moment.Moment = moment().add(1, 'year').endOf('year');

    hours: string[] = Array.from({length: 24}, (_, i) => i.toString().padStart(2, '0'));

    minutes: string[] = Array.from({length: 60}, (_, i) => i.toString().padStart(2, '0'));

    state!: AssignDialogStateInterface;

    groupToggle: boolean[] = [];

    itemsPerPage: number = 99;

    offset: number = 0;

    studentSelectionCounts: AppSelectCountsInterface = {groups: 0, options: 0};

    calendarFilter = (date: Date): boolean => {
        return moment(date).isSameOrAfter(moment(), 'day');
    };

    constructor(
        @Inject(MAT_DIALOG_DATA) data: AssignDialogDataInterface,
            dialogRef: MatDialogRef<AppExamAssignDialogComponent>,
            assignDialogService: AssignDialogService,
    ) {
        this.data = data;
        this.#dialogRef = dialogRef;
        this.#assignDialogService = assignDialogService;
        this.examAssignForm = new AppExamAssignDialogForm(data.examPlan);
    }

    ngOnInit() {
        this.#assignDialogService.subscribe((state: AssignDialogStateInterface) => this.#handleDialogState(state));
    }

    ngOnDestroy(): void {
        this.#assignDialogService.unsubscribe();
    }

    updateSelectionCounts(): void {
        const selectedStudents = this.examAssignForm.userIds.value;

        this.studentSelectionCounts = {
            groups: this.state.groups.map(
                (group: GroupInterface) => (group.students ?? []).map(
                    (student: UserInterface) => selectedStudents.includes(student.id),
                ).includes(false),
            ).filter((value: boolean) => !value).length,
            options: [... new Set(selectedStudents)].length,
        };
    }

    toggleGroup(index: number): void {
        this.groupToggle[index] = !this.groupToggle[index];
    }

    preventPropagation(event: Event): void {
        event.stopPropagation();
    }

    onBookSelectionChange(event: MatSelectChange): void {
        this.#assignDialogService.setBook(event.value);
    }

    onExamSelectionChange(event: MatSelectChange): void {
        this.#assignDialogService.setExam(event.value);
    }

    onSubmit(): void {
        const examAssignForm: AppExamAssignDialogForm = this.examAssignForm.validate();

        if (!examAssignForm.valid) {
            return;
        }

        this.#assignDialogService.submit(examAssignForm.getMode(), examAssignForm.book.value, {id: this.data.examPlan?.id, ...examAssignForm.getAll()})
            .subscribe((result: AssignDialogAssignResultInterface) => this.#handleSubmitResult(result));
    }

    #handleSubmitResult(result: AssignDialogAssignResultInterface): void {
        if (result.status) {
            this.#dialogRef.close(true);

            return;
        }

        const errorResponse = result?.errorResponse;

        if (ResponseValidationErrorTypeGuard.is(errorResponse)) {
            errorResponse.context.forEach((context: HttpValidationErrorResponseContextInterface) => this.examAssignForm.setErrorByContext(context));
        }
    }

    #handleDialogState(state: AssignDialogStateInterface): void {
        const previousBook = this.state?.book; // Track previous book

        this.state = state;

        this.examAssignForm.setTitle(state.exam?.title);
        this.examAssignForm.enableOrDisableField(AddExamFormFields.Book, 0 !== state.books.length);
        this.examAssignForm.enableOrDisableField(AddExamFormFields.Exam, 0 !== state.exams.length);
        this.examAssignForm.enableOrDisableField(AddExamFormFields.UserIds, 0 !== state.groups.length);

        if (this.data.examPlan) {
            const startDate = moment(this.data.examPlan.startDate);
            const endDate = moment(this.data.examPlan.endDate);

            this.examAssignForm.controls['startHour'].setValue(startDate.format('HH'));
            this.examAssignForm.controls['startMinute'].setValue(startDate.format('mm'));
            this.examAssignForm.controls['endHour'].setValue(endDate.format('HH'));
            this.examAssignForm.controls['endMinute'].setValue(endDate.format('mm'));
        }

        if (state.book?.uuid !== previousBook?.uuid) {
            this.examAssignForm.clearField(AddExamFormFields.Exam);
            this.examAssignForm.clearField(AddExamFormFields.UserIds);
        }

        this.updateSelectionCounts();
        this.#toggleMatDialogPanelLoadingClass(state.loading);
    }

    #toggleMatDialogPanelLoadingClass(loading: boolean): void {
        if (loading) {
            this.#dialogRef.addPanelClass('mat-dialog-container--loading');
        } else {
            this.#dialogRef.removePanelClass('mat-dialog-container--loading');
        }
    }
}
